import { useState, useEffect } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { BarChart3, TrendingUp, CalendarRange, ShieldCheck, Trophy, XCircle, CalendarClock, Copy, Mail, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const fmt = (n) => (n || 0).toLocaleString('ro-RO');

export default function ReportsPage() {
  const [weekly, setWeekly] = useState(null);
  const [winloss, setWinloss] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [feedUrl, setFeedUrl] = useState('');
  const [emailCfg, setEmailCfg] = useState(null);
  const [emailHistory, setEmailHistory] = useState([]);

  const loadEmail = () => {
    API.get('/report-email/config').then(r => setEmailCfg(r.data)).catch(() => {});
    API.get('/report-email/history').then(r => setEmailHistory(r.data)).catch(() => {});
  };

  useEffect(() => {
    API.get('/reports/weekly-pipeline').then(r => setWeekly(r.data)).catch(() => {});
    API.get('/reports/monthly-winloss').then(r => setWinloss(r.data)).catch(() => {});
    API.get('/reports/quarterly-compliance').then(r => setCompliance(r.data)).catch(() => {});
    API.get('/calendar/feed-url').then(r => setFeedUrl(`${process.env.REACT_APP_BACKEND_URL}${r.data.path}`)).catch(() => {});
    loadEmail();
  }, []);

  const copyFeed = () => { navigator.clipboard.writeText(feedUrl); toast.success('Link abonament copiat!'); };

  const setEmail = (key, val) => setEmailCfg(c => ({ ...c, emails: { ...c.emails, [key]: val } }));
  const toggleRecipient = (key) => setEmailCfg(c => {
    const has = c.weekly_recipients.includes(key);
    return { ...c, weekly_recipients: has ? c.weekly_recipients.filter(r => r !== key) : [...c.weekly_recipients, key] };
  });
  const saveEmailCfg = async () => {
    try {
      await API.put('/report-email/config', { emails: emailCfg.emails, weekly_recipients: emailCfg.weekly_recipients, enabled: emailCfg.enabled });
      toast.success('Configurare email salvata'); loadEmail();
    } catch { toast.error('Eroare salvare'); }
  };
  const sendNow = async () => {
    try { const r = await API.post('/report-email/send-now'); toast.success(`Raport generat (${r.data.status}) catre ${r.data.recipients.length} destinatari`); loadEmail(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
  };

  return (
    <div className="p-8" data-testid="reports-page">
      <div className="flex items-center gap-3 mb-6">
        <BarChart3 className="w-6 h-6 text-primary" />
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>Rapoarte Standard</h1>
      </div>

      <Card className="bg-primary/5 border-primary/20 mb-6" data-testid="calendar-feed-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1.5">
            <CalendarClock className="w-4 h-4 text-primary" />
            <p className="text-sm font-semibold" style={{ fontFamily: 'Outfit' }}>Abonament Calendar Echipa (auto-actualizat)</p>
          </div>
          <p className="text-xs text-muted-foreground mb-2">Adauga acest link o singura data in Google Calendar / Outlook / Apple ("Din URL"). Toate termenele extrase apar automat si se actualizeaza singure.</p>
          <div className="flex gap-2">
            <input readOnly value={feedUrl} className="flex-1 bg-zinc-900/50 border border-white/10 rounded px-3 h-9 text-xs font-mono-data text-muted-foreground" data-testid="feed-url-input" />
            <Button size="sm" variant="outline" onClick={copyFeed} disabled={!feedUrl} className="h-9 text-xs" data-testid="copy-feed-btn"><Copy className="w-3.5 h-3.5 mr-1.5" />Copiaza</Button>
          </div>
        </CardContent>
      </Card>

      {emailCfg && (
        <Card className="bg-card border-white/10 mb-6" data-testid="email-report-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Mail className="w-4 h-4 text-primary" />Rapoarte pe Email — Pipeline Saptamanal</CardTitle>
            <p className="text-[11px] text-muted-foreground">Se trimite automat lunea la 07:00 (ora Romaniei). Bifeaza cine primeste raportul. <span className="text-amber-400 font-semibold">Trimiterea este MOCK</span> (raportul se salveaza in aplicatie) pana configuram un furnizor de email.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {emailCfg.roles.map(role => (
                <div key={role.key} className="flex items-center gap-2" data-testid={`email-role-${role.key}`}>
                  <div className="flex items-center gap-2 w-44 shrink-0">
                    <Switch checked={emailCfg.weekly_recipients.includes(role.key)} onCheckedChange={() => toggleRecipient(role.key)} data-testid={`recipient-${role.key}`} />
                    <span className="text-xs text-muted-foreground">{role.label}</span>
                  </div>
                  <Input value={emailCfg.emails[role.key] || ''} onChange={e => setEmail(role.key, e.target.value)}
                    placeholder={`email ${role.label.toLowerCase()}`} className="bg-zinc-900/50 border-white/10 h-8 text-xs" data-testid={`email-input-${role.key}`} />
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2">
                <Switch checked={emailCfg.enabled} onCheckedChange={v => setEmailCfg(c => ({ ...c, enabled: v }))} data-testid="email-enabled-switch" />
                <span className="text-xs text-muted-foreground">Trimitere automata activa (luni 07:00)</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={sendNow} className="text-xs h-8" data-testid="send-report-now-btn"><Send className="w-3.5 h-3.5 mr-1.5" />Trimite acum (test)</Button>
                <Button size="sm" onClick={saveEmailCfg} className="text-xs h-8" data-testid="save-email-cfg-btn">Salveaza</Button>
              </div>
            </div>
            {emailHistory.length > 0 && (
              <div className="pt-2 border-t border-white/5">
                <p className="text-[10px] text-muted-foreground uppercase mb-1">Istoric ({emailHistory.length})</p>
                {emailHistory.slice(0, 4).map(h => (
                  <div key={h.id} className="flex items-center gap-2 text-[11px] py-0.5">
                    <Badge className={`text-[8px] ${h.status === 'mock' ? 'bg-amber-500/15 text-amber-400' : 'bg-emerald-500/15 text-emerald-400'}`}>{h.status}</Badge>
                    <span className="text-muted-foreground truncate flex-1">{h.subject}</span>
                    <span className="text-muted-foreground">{h.recipients?.length || 0} dest.</span>
                    <span className="text-muted-foreground">{new Date(h.created_at).toLocaleDateString('ro-RO')}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="weekly">
        <TabsList className="bg-zinc-900/50 border border-white/10 mb-6">
          <TabsTrigger value="weekly" data-testid="tab-weekly"><CalendarRange className="w-3.5 h-3.5 mr-1.5" />Pipeline Saptamanal</TabsTrigger>
          <TabsTrigger value="winloss" data-testid="tab-winloss"><TrendingUp className="w-3.5 h-3.5 mr-1.5" />Win/Loss Lunar</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance"><ShieldCheck className="w-3.5 h-3.5 mr-1.5" />Conformitate Trimestriala</TabsTrigger>
        </TabsList>

        {/* Weekly Pipeline */}
        <TabsContent value="weekly">
          {weekly && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-card border-white/10"><CardContent className="p-4"><p className="text-xs text-muted-foreground">Licitatii active</p><p className="text-2xl font-bold font-mono-data">{weekly.active_count}</p></CardContent></Card>
                <Card className="bg-card border-white/10"><CardContent className="p-4"><p className="text-xs text-muted-foreground">Valoare pipeline</p><p className="text-2xl font-bold font-mono-data text-primary">{fmt(weekly.total_pipeline_value)}</p></CardContent></Card>
                <Card className="bg-card border-white/10"><CardContent className="p-4"><p className="text-xs text-muted-foreground">Valoare ponderata (scor)</p><p className="text-2xl font-bold font-mono-data text-emerald-400">{fmt(Math.round(weekly.weighted_pipeline_value))}</p></CardContent></Card>
                <Card className="bg-card border-white/10"><CardContent className="p-4"><p className="text-xs text-muted-foreground">Termene saptamana asta</p><p className="text-2xl font-bold font-mono-data text-amber-400">{weekly.deadlines_this_week?.length || 0}</p></CardContent></Card>
              </div>

              <Card className="bg-card border-white/10">
                <CardHeader className="pb-2"><CardTitle className="text-sm" style={{ fontFamily: 'Outfit' }}>Termene in urmatoarele 7 zile</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {weekly.deadlines_this_week?.map((d, i) => (
                    <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-zinc-900/50 border border-white/5">
                      <Badge className={`text-[10px] ${d.days_left <= 2 ? 'bg-rose-500/20 text-rose-400' : 'bg-amber-500/20 text-amber-400'}`}>{d.days_left}z</Badge>
                      <span className="text-xs flex-1 truncate">{d.name}</span>
                      <span className="text-[10px] text-emerald-400 font-mono-data">{d.win_score}%</span>
                      <span className="text-[10px] text-muted-foreground font-mono-data">{d.deadline}</span>
                    </div>
                  ))}
                  {!weekly.deadlines_this_week?.length && <p className="text-xs text-muted-foreground text-center py-3">Niciun termen saptamana aceasta</p>}
                </CardContent>
              </Card>

              <Card className="bg-card border-white/10">
                <CardHeader className="pb-2"><CardTitle className="text-sm" style={{ fontFamily: 'Outfit' }}>Licitatii active (dupa scor)</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {weekly.active_projects?.map((p, i) => (
                    <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-zinc-900/50 border border-white/5">
                      <span className="text-xs flex-1 truncate">{p.name}</span>
                      <span className="text-[10px] text-muted-foreground font-mono-data">{fmt(p.value)} RON</span>
                      <div className="w-24"><Progress value={p.win_score} className="h-1.5" /></div>
                      <span className="text-[10px] text-emerald-400 font-mono-data w-8">{p.win_score}%</span>
                    </div>
                  ))}
                  {!weekly.active_projects?.length && <p className="text-xs text-muted-foreground text-center py-3">Nicio licitatie activa</p>}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Win/Loss */}
        <TabsContent value="winloss">
          {winloss && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-card border-white/10"><CardContent className="p-4"><p className="text-xs text-muted-foreground flex items-center gap-1"><Trophy className="w-3 h-3 text-emerald-400" />Castigate</p><p className="text-2xl font-bold font-mono-data text-emerald-400">{winloss.total_won}</p></CardContent></Card>
                <Card className="bg-card border-white/10"><CardContent className="p-4"><p className="text-xs text-muted-foreground flex items-center gap-1"><XCircle className="w-3 h-3 text-rose-400" />Pierdute</p><p className="text-2xl font-bold font-mono-data text-rose-400">{winloss.total_lost}</p></CardContent></Card>
                <Card className="bg-card border-white/10"><CardContent className="p-4"><p className="text-xs text-muted-foreground">Rata de castig</p><p className="text-2xl font-bold font-mono-data text-primary">{winloss.win_rate}%</p></CardContent></Card>
                <Card className="bg-card border-white/10"><CardContent className="p-4"><p className="text-xs text-muted-foreground">Valoare castigata</p><p className="text-2xl font-bold font-mono-data text-emerald-400">{fmt(winloss.value_won)}</p></CardContent></Card>
              </div>
              <Card className="bg-card border-white/10">
                <CardHeader className="pb-2"><CardTitle className="text-sm" style={{ fontFamily: 'Outfit' }}>Evolutie lunara</CardTitle></CardHeader>
                <CardContent>
                  {winloss.monthly?.length ? (
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-white/10 text-[10px] text-muted-foreground uppercase">
                      <th className="text-left py-2">Luna</th><th className="text-center">Castigate</th><th className="text-center">Pierdute</th><th className="text-right">Val. castigata</th></tr></thead>
                    <tbody>
                      {winloss.monthly.map((m, i) => (
                        <tr key={i} className="border-b border-white/5">
                          <td className="py-2 font-mono-data text-xs">{m.month}</td>
                          <td className="text-center text-emerald-400 font-mono-data">{m.castigat}</td>
                          <td className="text-center text-rose-400 font-mono-data">{m.pierdut}</td>
                          <td className="text-right font-mono-data text-xs">{fmt(m.value_won)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  ) : <p className="text-xs text-muted-foreground text-center py-3">Nu exista licitatii finalizate</p>}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Compliance */}
        <TabsContent value="compliance">
          {compliance && (
            <div className="space-y-4">
              {compliance.quarterly?.map((q, i) => (
                <Card key={i} className="bg-card border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold font-mono-data">{q.quarter}</p>
                      <Badge className={`text-[10px] ${q.compliance_rate >= 80 ? 'bg-emerald-500/20 text-emerald-400' : q.compliance_rate >= 50 ? 'bg-amber-500/20 text-amber-400' : 'bg-rose-500/20 text-rose-400'}`}>{q.compliance_rate}% conform</Badge>
                    </div>
                    <Progress value={q.compliance_rate} className="h-2 mb-2" />
                    <div className="flex gap-4 text-[10px] text-muted-foreground">
                      <span>{q.matrices} matrici</span><span>{q.total_items} cerinte</span>
                      <span className="text-emerald-400">{q.compliant} conforme</span><span className="text-rose-400">{q.non_compliant} neconforme</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {!compliance.quarterly?.length && <p className="text-sm text-muted-foreground text-center py-8">Nu exista matrici de conformitate generate. Genereaza matrici in tab-ul Instrumente al unei licitatii.</p>}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
